import { useState, useCallback, useMemo } from 'react';
import { Heart, ChevronLeft, ChevronRight, Sparkles, Quote } from 'lucide-react';
import { BIRTHDAY_WISHES } from '@/config/valentineContent';

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function Avatar({ wish }) {
  const [imgError, setImgError] = useState(false);
  const size = 64;

  if (wish.avatar && !imgError) {
    return (
      <div className="rounded-full overflow-hidden flex-shrink-0 mb-3"
        style={{ width: size, height: size, border: `2px solid ${wish.color || 'var(--accent-primary)'}` }}>
        <img
          src={wish.avatar}
          alt={wish.name || ''}
          onError={() => setImgError(true)}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      </div>
    );
  }

  return (
    <div className="bw-avatar flex-shrink-0 flex items-center justify-center rounded-full mb-3"
      style={{ backgroundColor: wish.color || 'var(--accent-primary)', width: size, height: size }}>
      <span className="font-heading text-white text-xl font-bold select-none">
        {wish.initials || '?'}
      </span>
    </div>
  );
}

export default function BirthdayWishes() {
  const wishes = useMemo(() => shuffle(BIRTHDAY_WISHES?.wishes || []), []);
  const [index, setIndex] = useState(0);
  const [anim, setAnim] = useState({ phase: 'in', dir: 'right' });
  const [likedIds, setLikedIds] = useState(new Set());

  const total = wishes.length;

  const goTo = useCallback((dir) => {
    const outDir = dir === 'right' ? 'left' : 'right';
    setAnim({ phase: 'out', dir: outDir });
    setTimeout(() => {
      setIndex(prev => {
        if (dir === 'left') return prev <= 0 ? total - 1 : prev - 1;
        return prev >= total - 1 ? 0 : prev + 1;
      });
      setAnim({ phase: 'in', dir });
    }, 250);
  }, [total]);

  const toggleLike = useCallback((id) => {
    setLikedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  if (total === 0) return null;

  const wish = wishes[index];
  if (!wish) return null;

  const liked = likedIds.has(wish.id);

  return (
    <section id="birthday-wishes" data-testid="birthday-wishes-section" className="section-romantic text-center">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="flex items-center justify-center gap-3 mb-2">
          <div className="w-12 h-[1px]" style={{ background: 'linear-gradient(90deg, transparent, var(--accent-light))' }} />
          <Sparkles size={14} style={{ color: 'var(--accent-primary)' }} />
          <div className="w-12 h-[1px]" style={{ background: 'linear-gradient(90deg, var(--accent-light), transparent)' }} />
        </div>
        <p className="font-body text-[10px] tracking-widest uppercase mb-2" style={{ color: 'var(--accent-light)' }}>
          {BIRTHDAY_WISHES?.sectionLabel}
        </p>
        <h2 className="font-heading text-2xl md:text-3xl mb-3" style={{ color: 'var(--text-primary)' }}>
          {BIRTHDAY_WISHES?.heading}
        </h2>

        {/* Counter */}
        <p className="font-body text-[10px] mb-6" style={{ color: 'var(--text-secondary)' }}>
          {index + 1} of {total}
        </p>

        {/* Card */}
        <div className="relative mb-6" style={{ minHeight: 240 }}>
          {/* Stacked bg cards */}
          <div className="bw-bg-card bw-bg-card-1" style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--accent-glow)' }} />
          <div className="bw-bg-card bw-bg-card-2" style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--accent-glow)' }} />

          {/* Main card */}
          <div
            className={`bw-main-card relative rounded-2xl p-6 sm:p-8 flex flex-col items-center text-center bw-${anim.phase}-${anim.dir}`}
            style={{ backgroundColor: 'var(--bg-card)', border: '1px solid var(--accent-glow)', boxShadow: '0 4px 24px var(--accent-glow)' }}>
            {/* Corner sparkle */}
            <div className="absolute top-3 right-3 opacity-15" style={{ color: 'var(--accent-light)' }}>
              <Sparkles size={10} />
            </div>

            {/* Avatar */}
            <Avatar wish={wish} />

            {/* Name */}
            <h3 className="font-heading text-base sm:text-lg mb-4" style={{ color: 'var(--text-primary)' }}>
              {wish.name || 'Unknown'}
            </h3>

            {/* Message */}
            <div className="relative flex-1 flex items-center">
              <Quote size={10} className="absolute -top-1 -left-1 opacity-20" style={{ color: 'var(--accent-primary)' }} />
              <p className="font-body text-sm sm:text-base leading-relaxed px-3" style={{ color: 'var(--text-secondary)' }}>
                {wish.message || ''}
              </p>
            </div>

            {/* Like button */}
            <button
              onClick={() => toggleLike(wish.id)}
              className={`bw-like-btn mt-5 flex items-center gap-1.5 px-4 py-2 rounded-full transition-all duration-200 hover:scale-105 active:scale-95 ${liked ? 'bw-liked' : ''}`}
              style={{
                background: liked ? 'var(--accent-glow)' : 'transparent',
                border: `1px solid ${liked ? 'var(--accent-primary)' : 'var(--accent-glow)'}`,
                cursor: 'pointer',
                color: liked ? 'var(--accent-primary)' : 'var(--accent-light)',
              }}>
              <Heart
                size={14}
                fill={liked ? 'var(--accent-primary)' : 'transparent'}
                stroke={liked ? 'var(--accent-primary)' : 'currentColor'}
                strokeWidth={1.5}
                className={liked ? 'bw-like-pop' : ''}
              />
              <span className="font-body text-xs">{liked ? 'Liked' : 'Like'}</span>
            </button>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-center gap-4">
          <button onClick={() => goTo('left')}
            className="w-10 h-10 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110 active:scale-95"
            style={{
              background: 'var(--accent-glow)',
              color: 'var(--accent-primary)',
              border: 'none',
              cursor: 'pointer',
            }}>
            <ChevronLeft size={18} />
          </button>

          {/* Dots */}
          <div className="flex items-center gap-1.5 flex-wrap justify-center" style={{ maxWidth: 200 }}>
            {wishes.map((w, i) => (
              <div
                key={w?.id || i}
                className="rounded-full transition-all duration-300"
                style={{
                  width: i === index ? 8 : 5,
                  height: i === index ? 8 : 5,
                  backgroundColor: i === index ? 'var(--accent-primary)' : 'var(--accent-light)',
                }}
              />
            ))}
          </div>

          <button onClick={() => goTo('right')}
            className="w-10 h-10 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110 active:scale-95"
            style={{
              background: 'var(--accent-glow)',
              color: 'var(--accent-primary)',
              border: 'none',
              cursor: 'pointer',
            }}>
            <ChevronRight size={18} />
          </button>
        </div>
      </div>
    </section>
  );
}
